import { format } from 'date-fns';
import * as Linking from 'expo-linking';
import Linkify from 'react-native-linkify';
import { twMerge } from 'tailwind-merge';

import { InquiryGroupSummaryCard } from './InquiryGroupSummaryCard';
import { readInquiryLibraryAnswers } from '../../model/answers';
import type { LibraryPost } from '../../model/types';
import { InquiryLibraryAnswersBody } from '../answers/InquiryLibraryAnswersBody';
import { postDateClassName } from '../answers/answersLayout';

import { Highlight } from '@/components/Highlight';
import { Heading } from '@/components/heading';
import { Badge, BadgeText } from '@/components/ui/badge';
import { HStack } from '@/components/ui/hstack';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import type { InquirySummarySource } from '@/util/analyticsEvents';

/**
 * An Inquiry Library post as a reader sees it: cancer type and date, the
 * title, the structured inquiry summary, then the body. With
 * INQUIRY_LIBRARY_ANSWERS on and answers data present, the body is replaced by the
 * question and one block per specialist, never both. The summary card stays at
 * the top, above the question, as a quick tip, in its `inquiryLibraryAnswers` variant, so
 * the conversation below it stays together.
 *
 * Shared by the post screen and Research's result pane, so a post reads
 * the same wherever it opens. Layout only through `className`; the type scale
 * and the order of the parts belong here.
 *
 * `source` names the surface, so the summary card reports where it was read
 * and takes Research's compact form there.
 */
export function CommunityPostContent({
  post,
  className,
  source = 'community',
  searchQuery,
}: {
  post: LibraryPost;
  className?: string;
  source?: InquirySummarySource;
  /**
   * The search the post was opened from. The answers view marks its words itself; the plain body
   * already carries the search engine's marks in `displayBody`.
   */
  searchQuery?: string;
}) {
  const isInquirySummaryEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_SUMMARY);

  const isInquiryLibraryAnswersEnabled = useFeatureFlag(FEATURE_FLAGS.INQUIRY_LIBRARY_ANSWERS);

  /** Null whenever the flag is off or the post has no answers data, which keeps the body. */
  const inquiryLibraryAnswers = isInquiryLibraryAnswersEnabled
    ? readInquiryLibraryAnswers(post)
    : null;

  const publishedAtDate = post.publishedAt ? format(new Date(post.publishedAt), 'MM/dd/yyyy') : '';

  const titleHTML = post.displayTitle;

  /** Keyed on the post so another one starts from the layout's default, unrated. */
  const summaryCard =
    isInquirySummaryEnabled && post.summary ? (
      <InquiryGroupSummaryCard
        key={post.id}
        summary={post.summary}
        postId={post.id}
        source={source}
        variant={inquiryLibraryAnswers ? 'inquiryLibraryAnswers' : 'default'}
      />
    ) : null;

  const bodyHTML = post.displayBody;

  return (
    <VStack className={twMerge('gap-4', className)}>
      <HStack className="flex-wrap items-center justify-between gap-x-4 gap-y-1">
        <Badge action="muted" size="md">
          <BadgeText>{post.cancerType?.shortLabel || post.cancerType?.label || ''}</BadgeText>
        </Badge>
        <Text size="sm" className={postDateClassName(inquiryLibraryAnswers !== null)}>
          {publishedAtDate}
        </Text>
      </HStack>
      <Heading size="h2-md" className="prose prose-lg font-semibold">
        <Highlight rawHTML={titleHTML} />
      </Heading>
      {summaryCard}
      {inquiryLibraryAnswers ? (
        <InquiryLibraryAnswersBody
          post={inquiryLibraryAnswers}
          postId={post.id}
          searchQuery={searchQuery}
        />
      ) : (
        <Linkify
          linkStyle={{ textDecorationLine: 'underline' }}
          onPress={(url) => Linking.openURL(url)}>
          <Text className="font-body text-base leading-relaxed">
            <Highlight rawHTML={bodyHTML} />
          </Text>
        </Linkify>
      )}
    </VStack>
  );
}
